import React, { useEffect, useState } from 'react';
import {
  TextField, Button, Grid2, Alert, FormControl,
  Autocomplete, Checkbox, Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportServices } from '../../services/reportServices';
import { useAuth } from '../../services/authentication/authContext';
import Clear from '@mui/icons-material/Clear';
import GetApp from '@mui/icons-material/GetApp';
import { userServices } from '../../services/userServices';
import { downloadReport } from '../../utils/downloadReport ';
import '../../css/Reports.css';
import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import ReportGenerateButton from '../common/ReportGenerateButton';
import PageHeader from '../ui/PageHeader';

const LeaveReport = ({ showHeader = true }) => {
  const [userList, setUserList] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const { accessToken } = useAuth();

  // Load user list on mount
  useEffect(() => {
    if (accessToken) {
      setLoading(true);
      userServices.getUsersByRoleName()
        .then((response) => {
          setUserList(response);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [accessToken]);

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setSelectedUserIds([]);
  };

  const handleReport = () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (startDate > endDate) {
      setError('Start date must be earlier than or equal to end date.');
      setTimeout(() => setError(null), 4000);
      return;
    }

    setGenerating(true);
    const formData = { startDate, endDate, userIds: selectedUserIds };

    reportServices.downloadLeaveReport(formData)
      .then((response) => {
        downloadReport(response);
        setGenerating(false);
        setSuccessMessage('Report Generated Successfully!');
        setTimeout(() => setSuccessMessage(null), 4000);
        handleClear();
      })
      .catch((err) => {
        setError(err?.response?.data?.message || err.message || 'Failed to generate report.');
        setTimeout(() => setError(null), 4000);
        setGenerating(false);
      });
  };

  const isAllSelected = selectedUserIds.length === userList.length && userList.length > 0;
  const canGenerate = Boolean(startDate && endDate) && !loading && !generating;

  return (
    <div className={showHeader ? 'reports-container' : ''}>
      {showHeader && <PageHeader title="Leave Report" />}

      <Box className={showHeader ? 'report-box' : ''}>

        {successMessage && (
          <Alert variant="filled" severity="success" style={{ marginBottom: '1.25rem' }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert variant="outlined" severity="error" style={{ marginBottom: '1.25rem' }}>
            {error}
          </Alert>
        )}

        <form style={{ marginTop: '1.25rem' }}>
          <Grid2 container spacing={3}>

            {/* Start Date */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                type="date"
                label={t('report.startDate')}
                variant="outlined"
                fullWidth
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                margin="normal"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid2>

            {/* End Date */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                type="date"
                label={t('report.endDate')}
                variant="outlined"
                fullWidth
                required
                value={endDate}
                disabled={!startDate}
                onChange={(e) => setEndDate(e.target.value)}
                margin="normal"
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { min: startDate },
                }}
              />
            </Grid2>

            {/* User multi-select */}
            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  multiple
                  id="leave-report-user-select"
                  options={[{ id: 'all', firstName: 'Select All' }, ...userList]}
                  disableCloseOnSelect
                  value={userList.filter((u) => selectedUserIds.includes(u.id))}
                  getOptionLabel={(u) =>
                    u.id === 'all'
                      ? 'Select All'
                      : `${u.firstName} ${u.middleName ?? ''} ${u.lastName}`.trim()
                  }
                  onChange={(_, newValue) => {
                    if (newValue.some((u) => u.id === 'all')) {
                      setSelectedUserIds(
                        isAllSelected ? [] : userList.map((u) => u.id)
                      );
                    } else {
                      setSelectedUserIds(
                        newValue.filter((u) => u.id !== 'all').map((u) => u.id)
                      );
                    }
                  }}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                        checkedIcon={<CheckBox fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={option.id === 'all' ? isAllSelected : selected}
                      />
                      {option.id === 'all'
                        ? 'Select All'
                        : `${option.firstName} ${option.middleName ?? ''} ${option.lastName}`.trim()}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User"
                    />
                  )}
                />
              </FormControl>
            </Grid2>

            {/* Buttons */}
            <Grid2 size={{ xs: 12 }} className="gridContainer">
              <ReportGenerateButton
                onClick={handleReport}
                loading={generating}
                disabled={!canGenerate}
                className={`commonButton ${canGenerate ? 'reportButtonSuccess' : 'reportButtonDefault'}`}
                icon={<GetApp className="buttonIcon" />}
                label={t('job.report')}
                loadingLabel="Preparing your report..."
              />
              <Button
                type="button"
                variant="contained"
                onClick={handleClear}
                className="commonButton clearButton"
                disabled={loading || generating}
              >
                <Clear className="buttonIcon" /> {t('job.clear')}
              </Button>
            </Grid2>

          </Grid2>
        </form>
      </Box>
    </div>
  );
};

export default LeaveReport;
